import {Box, Typography} from '@mui/material';
import {BlockRounded, CheckRounded, LoginRounded} from '@mui/icons-material';
import moment, {Moment} from 'moment';
import {useTranslation} from 'react-i18next';
import './attendance-session-card.scss';
import {AttendanceManualBadge} from './attendance-manual-notice';
import AttendanceStatusChip from './attendance-status-chip';
import {AttendanceTrackerSession} from '../../backend/responses/attendance-tracker-session';
import {translations} from '../../i18n/translations';
import {
  formatSessionDuration,
  getSessionDurationSeconds,
  getSessionState,
} from '../../utils/functions/attendance-sessions';

type AttendanceSessionCardProps = {
  session: AttendanceTrackerSession;
  /** Current time according to the server, to know how long an open session is running */
  now: Moment;
  /** Day being listed. Dates are only shown for the movements of other days. */
  selectedDay: Moment;
  /** The card is clickable only when it can be selected (e.g. to open its details) */
  onSelect?: (session: AttendanceTrackerSession) => void;
  /** Name of the professional of the session, when the sessions of several professionals are listed */
  professionalName?: string;
};

/** "08:02", or "25/10 22:00" when it is not in the day being listed */
const formatMovement = (date: string, selectedDay: Moment): string =>
  moment(date).isSame(selectedDay, 'day')
    ? moment(date).format('HH:mm')
    : moment(date).format('DD/MM HH:mm');

export default function AttendanceSessionCard({
  session,
  now,
  selectedDay,
  onSelect,
  professionalName,
}: AttendanceSessionCardProps) {
  const {t} = useTranslation();
  const texts = translations.daily_care_log.attendance_tracker.session;

  const state = getSessionState(session, now);
  const isOpen = state === 'running' || state === 'overdue';
  const duration = formatSessionDuration(getSessionDurationSeconds(session, now));

  return (
    <Box
      className={`attendance-session-card attendance-session-card-${state}${onSelect ? ' attendance-session-card-clickable' : ''}`}
      onClick={() => onSelect?.(session)}
    >
      <Box className="card-icon-container">
        <Box className="card-icon">
          {state === 'voided' ? (
            <BlockRounded />
          ) : state === 'closed' ? (
            <CheckRounded />
          ) : (
            <LoginRounded />
          )}
        </Box>
      </Box>

      <Box className="card-session-container">
        {professionalName && (
          <Typography className="professional-name">{professionalName}</Typography>
        )}
        <Typography className="movement">
          <span className="movement-part">
            {t(texts.entry)}:{' '}
            <span className="time">{formatMovement(session.entry.registeredAt, selectedDay)}</span>
          </span>
          <span className="movement-separator">{' | '}</span>
          <span className="movement-part">
            {t(texts.exit)}:{' '}
            <span className="time">
              {session.exit ? formatMovement(session.exit.registeredAt, selectedDay) : '---'}
            </span>
          </span>
        </Typography>
        <Box className="details">
          {/* A voided session is not time worked */}
          {state !== 'voided' && (
            <Typography className="duration">
              {isOpen ? t(texts.elapsed, {duration}) : duration}
            </Typography>
          )}
          <Typography className="date">
            {moment(session.entry.registeredAt).format('DD/MM')}
          </Typography>
        </Box>
        {(session.entry.source === 'manual' || session.exit?.source === 'manual') && (
          <Box className="manual-badges">
            <AttendanceManualBadge point={session.entry} movement="entry" />
            <AttendanceManualBadge point={session.exit} movement="exit" />
          </Box>
        )}
      </Box>

      <Box className="card-status-container">
        <AttendanceStatusChip state={state} />
      </Box>
    </Box>
  );
}
